// Markdown-lite: the small subset the case form's narrative fields support.
//   **bold**   *italic* / _italic_   [text](https://…)   "- " / "* " bullets   "1. " numbered
// Text is stored exactly as typed (plain strings); this parses it for display. Anything the
// parser doesn't recognise is shown verbatim, so old free text renders unchanged. The PDF has a
// matching parser in ipri-backend/src/utils/casePdf.js — keep the two in step.

export type InlineNode =
  | { type: 'text'; text: string }
  | { type: 'strong'; children: InlineNode[] }
  | { type: 'em'; children: InlineNode[] }
  | { type: 'link'; href: string; children: InlineNode[] };

export type BlockNode =
  | { type: 'paragraph'; children: InlineNode[] }
  | { type: 'list'; ordered: boolean; items: InlineNode[][] };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const LINK = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/;

const isWordChar = (ch: string | undefined) => !!ch && /[\p{L}\p{N}]/u.test(ch);

// Finds the closing single-character delimiter for italics, skipping "**" pairs so that
// "*a **b** c*" nests correctly. Returns -1 when the run never closes.
const findCloser = (s: string, ch: string, from: number): number => {
  for (let j = from; j < s.length; j += 1) {
    if (s[j] !== ch) continue;
    if (ch === '*' && (s[j + 1] === '*' || s[j - 1] === '*')) continue;
    if (/\s/.test(s[j - 1] || '')) continue;                // "* not a closer"
    if (ch === '_' && isWordChar(s[j + 1])) continue;       // snake_case_words stay plain
    return j;
  }
  return -1;
};

export const parseInline = (s: string): InlineNode[] => {
  const out: InlineNode[] = [];
  let buf = '';
  const flush = () => { if (buf) { out.push({ type: 'text', text: buf }); buf = ''; } };
  let i = 0;
  while (i < s.length) {
    const ch = s[i];
    if (ch === '[') {
      const m = LINK.exec(s.slice(i));
      if (m) { flush(); out.push({ type: 'link', href: m[2], children: parseInline(m[1]) }); i += m[0].length; continue; }
    }
    if (ch === '*' && s[i + 1] === '*' && !/\s/.test(s[i + 2] || ' ')) {
      const end = s.indexOf('**', i + 2);
      if (end > i + 2 && !/\s/.test(s[end - 1])) {
        flush(); out.push({ type: 'strong', children: parseInline(s.slice(i + 2, end)) }); i = end + 2; continue;
      }
    }
    if ((ch === '*' || ch === '_') && !/\s/.test(s[i + 1] || ' ') && !(ch === '_' && isWordChar(s[i - 1]))) {
      const end = findCloser(s, ch, i + 1);
      if (end > i + 1) {
        flush(); out.push({ type: 'em', children: parseInline(s.slice(i + 1, end)) }); i = end + 1; continue;
      }
    }
    buf += ch;
    i += 1;
  }
  flush();
  return out;
};

export const parseMarkdownLite = (text: string | null | undefined): BlockNode[] => {
  if (!text) return [];
  const blocks: BlockNode[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const closePara = () => { if (para.length) { blocks.push({ type: 'paragraph', children: parseInline(para.join('\n')) }); para = []; } };
  const closeList = () => { if (list) { blocks.push({ type: 'list', ordered: list.ordered, items: list.items.map(parseInline) }); list = null; } };

  text.replace(/\r\n?/g, '\n').split('\n').forEach((line) => {
    if (!line.trim()) { closePara(); closeList(); return; }
    const b = BULLET.exec(line);
    const n = b ? null : NUMBERED.exec(line);
    if (b || n) {
      const ordered = !!n;
      closePara();
      if (list && list.ordered !== ordered) closeList();
      if (!list) list = { ordered, items: [] };
      list.items.push((b || n)![1]);
      return;
    }
    closeList();
    para.push(line);
  });
  closePara();
  closeList();
  return blocks;
};

// Plain-text projection (for search, previews and length checks).
export const inlineToPlain = (nodes: InlineNode[]): string =>
  nodes.map((n) => (n.type === 'text' ? n.text : inlineToPlain(n.children))).join('');

export const markdownToPlain = (text: string | null | undefined): string =>
  parseMarkdownLite(text)
    .map((b) => (b.type === 'paragraph' ? inlineToPlain(b.children) : b.items.map((it) => `• ${inlineToPlain(it)}`).join('\n')))
    .join('\n\n');
